import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { useMatches } from 'react-router-dom';

import useLocalStorageState from '~/shared/hooks/useLocalStorageState';

type BooleanStateAction = Dispatch<SetStateAction<boolean>>;

interface Props {
  isMainNavigationCollapsed: boolean;
  setIsMainNavigationCollapsed: BooleanStateAction;
}

const UiStateContext = createContext<Props>({
  isMainNavigationCollapsed: true,
  setIsMainNavigationCollapsed: () => {},
});

export const useUiState = () => useContext(UiStateContext);

const UiStateContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const routeUiState = useNavigationUiStateFromRouteHandle();
  const [isLocalCollapsed, setIsLocalCollapsed] = useLocalStorageState<boolean>(
    'MainNavigation.isCollapsed',
    true,
  );
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    routeUiState ? routeUiState === 'collapsed' : isLocalCollapsed,
  );

  useLayoutEffect(() => {
    if (routeUiState) {
      setIsCollapsed(routeUiState === 'collapsed');
    } else {
      // if no route ui state overrides the current default setting we
      // reset to the user preference
      setIsCollapsed(isLocalCollapsed);
    }
  }, [routeUiState, isLocalCollapsed]);

  const setCollapsedSync: BooleanStateAction = useCallback(
    (cb: boolean | ((p: boolean) => boolean)) =>
      setIsCollapsed((prev) => {
        const isCollapsedFinal = typeof cb === 'boolean' ? cb : cb(prev);
        setIsLocalCollapsed(isCollapsedFinal);
        return isCollapsedFinal;
      }),
    [setIsCollapsed, setIsLocalCollapsed],
  );

  return (
    <UiStateContext.Provider
      value={{
        isMainNavigationCollapsed: isCollapsed,
        setIsMainNavigationCollapsed: setCollapsedSync,
      }}
    >
      {children}
    </UiStateContext.Provider>
  );
};

export default UiStateContextProvider;

const isNavigationUiStateHandle = (handle: unknown): handle is { navigationUiState: 'collapsed' } =>
  typeof handle === 'object' && handle !== null && 'navigationUiState' in handle;

export const useNavigationUiStateFromRouteHandle = () => {
  const matches = useMatches();
  const match = matches[matches.length - 1];

  if (match && isNavigationUiStateHandle(match.handle)) {
    return match.handle.navigationUiState;
  }
  return undefined;
};
